import type { AutocompleteProps } from './autocomplete';
import { Autocomplete } from './autocomplete';
import { TextField } from './text-field';

type BaseItem = { id: string };

type MultiSelectProps<T extends BaseItem> = {
  options: T[];
  value: T[];
  onChange: (newValue: T[]) => void;
  getOptionLabel: (option: T) => string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  required?: boolean;
  filterSelectedOptions?: boolean;
  renderValue?: AutocompleteProps<T, true, false, false>['renderValue'];
  renderOption?: AutocompleteProps<T, true, false, false>['renderOption'];
  sx?: AutocompleteProps<T, true, false, false>['sx'];
};

export const MultiSelect = <T extends BaseItem>({
  options,
  value,
  onChange,
  getOptionLabel,
  label,
  placeholder,
  disabled = false,
  loading = false,
  error = false,
  helperText,
  fullWidth = false,
  required = false,
  filterSelectedOptions = false,
  renderValue,
  renderOption,
  sx
}: MultiSelectProps<T>) => (
  <Autocomplete
    multiple
    options={options}
    value={value}
    onChange={(_, newValue) => onChange(newValue)}
    getOptionLabel={getOptionLabel}
    getOptionKey={option => option.id}
    isOptionEqualToValue={(option, val) => option.id === val.id}
    filterSelectedOptions={filterSelectedOptions}
    disabled={disabled}
    loading={loading}
    disableCloseOnSelect
    fullWidth={fullWidth}
    renderInput={params => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        required={required}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    )}
    renderValue={renderValue}
    renderOption={renderOption}
    sx={sx}
  />
);
